import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import usePageMeta from '@/hooks/usePageMeta';
import { SEO_ROUTES } from '@/src/seo/routes';
import MarketingHeader from '@/components/marketing/MarketingHeader';
import MarketingFooter from '@/components/marketing/MarketingFooter';
import { CONTACT_EMAIL, SUPPORT_EMAIL } from '@/components/marketing/marketingNav';
import { CONTACT_TOPICS } from '@/components/marketing/siteFacts';
import { getWeaveinApiUrl } from '@/src/config';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Loader2,
  LifeBuoy,
  Mail,
  AlertTriangle,
} from 'lucide-react';

/**
 * Contact page.
 *
 * Posts to `POST /api/public/contact`, which files the inquiry in
 * contact_submissions and emails the team.
 *
 * It calls `getWeaveinApiUrl` rather than a bare `/api/...` path on purpose.
 * A relative path on weavein.ai is rewritten by vercel.json to
 * trust-marketplace-alov, which is the DEVELOPMENT backend — a contact form
 * wired that way would file production inquiries into the dev database. The
 * helper resolves to weavein-api in production and stays relative on
 * dev.weavein.ai, where relative is what we want.
 */

/**
 * The topics are the site's three doors plus the requests other pages link
 * here with (`/contact?topic=sandbox`, `?topic=guide&note=Hub%20guide`), so a
 * visitor who clicked "Request the hub guide" lands with it already chosen.
 */
const TOPICS = CONTACT_TOPICS;
const isTopic = (v: string | null) => TOPICS.some((t) => t.value === v);

type Status = 'idle' | 'sending' | 'sent' | 'failed';

const FIELD =
  'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 ' +
  'px-4 py-3 text-gray-900 dark:text-gray-100 placeholder-gray-400 ' +
  'focus:outline-none focus:ring-2 focus:ring-v2accent focus:border-transparent';

const LABEL = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5';

export default function ContactPage() {
  usePageMeta(SEO_ROUTES['/contact']);

  const [params] = useSearchParams();
  const topicParam = params.get('topic');
  const note = params.get('note');
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    type: isTopic(topicParam) ? (topicParam as string) : 'general',
    message: note ? `About: ${note}\n\n` : '',
  });
  const [status, setStatus] = useState<Status>('idle');

  const set = (key: keyof typeof form) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => setForm((f) => ({ ...f, [key]: e.target.value }));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');

    try {
      const res = await fetch(getWeaveinApiUrl('/api/public/contact'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim() || null,
          phone: form.phone.trim() || null,
          message: form.message.trim(),
          type: form.type,
          source: typeof window !== 'undefined' ? window.location.href : null,
        }),
      });
      if (!res.ok) throw new Error(`Contact form returned ${res.status}`);
      setStatus('sent');
    } catch (err) {
      // Never swallow this. If the post failed, the message exists nowhere —
      // the fallback below is the only thing standing between the visitor and
      // a lost inquiry.
      console.error('Contact form submission failed', err);
      setStatus('failed');
    }
  }

  /** A mailto carrying what they already typed, for when the post fails. */
  const mailtoFallback = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
 `WeaveIn inquiry from ${form.name || 'the website'}`,
 )}&body=${encodeURIComponent(form.message)}`;

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <MarketingHeader />

      {/* Hero */}
      <section className="bg-paper border-b border-v2border py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-ink mb-5">
              Talk to <span className="text-v2accent">us</span>
            </h1>
            <p className="text-xl text-ink-500">
              Tell us what you are trying to connect — a referral that never closes,
              a program you cannot bill for, a population you cannot reach. We will
              come back to you with whether WeaveIn is the right fit, and say so if
              it is not.
            </p>
          </div>
        </div>
      </section>

      {/* Form + direct contact */}
      <section className="py-16 md:py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Form */}
            <div className="lg:col-span-2">
              {status === 'sent' ? (
                <div
                  role="status"
                  className="rounded-2xl border border-v2accent bg-v2accent-soft p-8"
                >
                  <CheckCircle2 className="w-10 h-10 text-v2accent mb-4" />
                  <h2 className="text-2xl font-bold text-ink dark:text-white mb-3">
                    Thank you — we have it
                  </h2>
                  <p className="text-gray-600 dark:text-gray-300 mb-6">
                    Your message is with our team and someone will reply to{' '}
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {form.email}
                    </span>
                    , usually within one business day.
                  </p>
                  <Link
                    to="/platform"
                    className="inline-flex items-center gap-2 text-ink font-medium hover:underline"
                  >
                    While you wait, see how the platform works
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate={false}>
                  <h2 className="text-2xl font-bold text-ink dark:text-white mb-6">
                    Send us a message
                  </h2>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className={LABEL} htmlFor="contact-name">
                        Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="contact-name"
                        className={FIELD}
                        value={form.name}
                        onChange={set('name')}
                        required
                        autoComplete="name"
                      />
                    </div>
                    <div>
                      <label className={LABEL} htmlFor="contact-email">
                        Work email <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        className={FIELD}
                        value={form.email}
                        onChange={set('email')}
                        required
                        autoComplete="email"
                      />
                    </div>
                    <div>
                      <label className={LABEL} htmlFor="contact-company">
                        Organization
                      </label>
                      <input
                        id="contact-company"
                        className={FIELD}
                        value={form.company}
                        onChange={set('company')}
                        autoComplete="organization"
                      />
                    </div>
                    <div>
                      <label className={LABEL} htmlFor="contact-phone">
                        Phone
                      </label>
                      <input
                        id="contact-phone"
                        type="tel"
                        className={FIELD}
                        value={form.phone}
                        onChange={set('phone')}
                        autoComplete="tel"
                      />
                    </div>
                  </div>

                  <div className="mt-5">
                    <label className={LABEL} htmlFor="contact-type">
                      What is this about?
                    </label>
                    <select
                      id="contact-type"
                      className={FIELD}
                      value={form.type}
                      onChange={set('type')}
                    >
                      {TOPICS.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mt-5">
                    <label className={LABEL} htmlFor="contact-message">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      className={`${FIELD} min-h-[160px] resize-y`}
                      value={form.message}
                      onChange={set('message')}
                      required
                      placeholder="Who you serve, and what is not working today."
                    />
                  </div>

                  {status === 'failed' && (
                    <div
                      role="alert"
                      className="mt-5 flex gap-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 p-4"
                    >
                      <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-900 dark:text-amber-200">
                        <p className="font-medium mb-1">We could not send that.</p>
                        <p>
                          Nothing was saved, so please don't assume it reached us. Try
                          again, or{' '}
                          <a href={mailtoFallback} className="underline font-medium">
                            email {CONTACT_EMAIL}
                          </a>{' '}
                          — the link carries what you typed.
                        </p>
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'sending'}
                    className="mt-7 inline-flex items-center justify-center gap-2 rounded-lg bg-ink px-7 py-3.5 text-base font-semibold text-paper transition-colors hover:bg-ink-700 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status === 'sending' ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        Send message
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>

                  <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                    We use what you send here to reply to you. Nothing more.
                  </p>
                </form>
              )}
            </div>

            {/* Direct contact */}
            <aside className="lg:col-span-1">
              <div className="rounded-2xl bg-gray-50 dark:bg-gray-800 p-8">
                <h2 className="text-lg font-bold text-ink dark:text-white mb-6">
                  Or reach us directly
                </h2>

                <div className="flex gap-3 mb-6">
                  <Mail className="w-5 h-5 text-v2accent flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      New inquiries
                    </p>
                    <a
                      href={`mailto:${CONTACT_EMAIL}`}
                      className="text-sm text-v2accent hover:underline break-all"
                    >
                      {CONTACT_EMAIL}
                    </a>
                  </div>
                </div>

                <div className="flex gap-3">
                  <LifeBuoy className="w-5 h-5 text-v2accent flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      Already a customer
                    </p>
                    <a
                      href={`mailto:${SUPPORT_EMAIL}`}
                      className="text-sm text-v2accent hover:underline break-all"
                    >
                      {SUPPORT_EMAIL}
                    </a>
                  </div>
                </div>

                <hr className="my-7 border-gray-200 dark:border-gray-700" />

                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                  Looking for something specific?
                </p>
                <ul className="space-y-2 text-sm">
                  <li>
                    <Link to="/platform" className="text-ink hover:underline">
                      What the platform does
                    </Link>
                  </li>
                  <li>
                    <Link to="/impact" className="text-ink hover:underline">
                      The results, measured
                    </Link>
                  </li>
                  <li>
                    <Link to="/funder-coverage" className="text-ink hover:underline">
                      Which funders are covered
                    </Link>
                  </li>
                  <li>
                    <Link to="/developers" className="text-ink hover:underline">
                      API and developers
                    </Link>
                  </li>
                  <li>
                    <Link to="/company/team" className="text-ink hover:underline">
                      Who we are
                    </Link>
                  </li>
                </ul>
              </div>
            </aside>
          </div>

          <div className="mt-14">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-ink"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to home
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
